/**
 * Active project + board context (Milestone 2.6).
 *
 * The active org id is owned by ActiveOrgContext. Below it, we layer:
 *   - active project id (per-org, persisted in localStorage)
 *   - active board id (per-project, persisted in localStorage)
 *
 * On org change → reset project + board.
 * On project change → reset board.
 *
 * Persists keys:
 *   - chaos.active_project_id
 *   - chaos.active_board_id
 *
 * Consumers: <ProjectSwitcher/>, <BoardTabs/>, optional deep-link
 * resolution in route loaders.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { boardsApi, boardsKeys } from './api/boards';
import { projectsV2Api, projectsV2Keys } from './api/projects-v2';
import { useActiveOrg } from './active-org';

const PROJECT_KEY = 'chaos.active_project_id';
const BOARD_KEY = 'chaos.active_board_id';

function readStored(k: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function writeStored(k: string, v: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}

export interface ActiveProjectContextValue {
  activeProjectId: string | null;
  activeBoardId: string | null;
  setActiveProject: (id: string | null) => void;
  setActiveBoard: (id: string | null) => void;
}

const ActiveProjectContext = createContext<ActiveProjectContextValue | undefined>(undefined);

export function ActiveProjectProvider({ children }: { children: ReactNode }) {
  const { activeOrgId } = useActiveOrg();
  const qc = useQueryClient();

  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(() =>
    readStored(PROJECT_KEY),
  );
  const [activeBoardId, setActiveBoardIdState] = useState<string | null>(() =>
    readStored(BOARD_KEY),
  );

  // Resolve projects for the active org so we can validate selection.
  const projectsQ = useQuery({
    queryKey: activeOrgId
      ? projectsV2Keys.list(activeOrgId)
      : ['projects-v2', 'list', '_none'],
    queryFn: () => (activeOrgId ? projectsV2Api.list(activeOrgId) : Promise.resolve([])),
    enabled: !!activeOrgId,
    staleTime: 30_000,
  });

  // Resolve boards for the active project.
  const boardsQ = useQuery({
    queryKey:
      activeOrgId && activeProjectId
        ? boardsKeys.listInProject(activeOrgId, activeProjectId)
        : ['boards-v2', 'list', '_none'],
    queryFn: () =>
      activeOrgId && activeProjectId
        ? boardsApi.list(activeOrgId, activeProjectId)
        : Promise.resolve([]),
    enabled: !!activeOrgId && !!activeProjectId,
    staleTime: 30_000,
  });

  const projects = projectsQ.data ?? [];
  const boards = boardsQ.data ?? [];

  // Reset project when org changes or stored id missing.
  useEffect(() => {
    if (!activeOrgId) {
      setActiveProjectIdState(null);
      setActiveBoardIdState(null);
      return;
    }
    if (!projects.length) return;
    if (!activeProjectId || !projects.some((p) => p.id === activeProjectId)) {
      const fallback = projects[0]?.id ?? null;
      setActiveProjectIdState(fallback);
      writeStored(PROJECT_KEY, fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId, projects.length]);

  // Reset board when project changes or stored id missing in current list.
  useEffect(() => {
    if (!activeProjectId) {
      setActiveBoardIdState(null);
      return;
    }
    if (!boards.length) return;
    if (!activeBoardId || !boards.some((b) => b.id === activeBoardId)) {
      const fallback = boards[0]?.id ?? null;
      setActiveBoardIdState(fallback);
      writeStored(BOARD_KEY, fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, boards.length]);

  const setActiveProject = useCallback(
    (id: string | null) => {
      setActiveProjectIdState(id);
      writeStored(PROJECT_KEY, id);
      // Drop board state — will repopulate when boards list lands.
      setActiveBoardIdState(null);
      writeStored(BOARD_KEY, null);
      // Invalidate cards/columns for prior project.
      void qc.invalidateQueries({ queryKey: ['boards-v2'] });
      void qc.invalidateQueries({ queryKey: ['cards-v2'] });
      void qc.invalidateQueries({ queryKey: ['column-triggers'] });
    },
    [qc],
  );

  const setActiveBoard = useCallback(
    (id: string | null) => {
      setActiveBoardIdState(id);
      writeStored(BOARD_KEY, id);
    },
    [],
  );

  const value = useMemo<ActiveProjectContextValue>(
    () => ({ activeProjectId, activeBoardId, setActiveProject, setActiveBoard }),
    [activeProjectId, activeBoardId, setActiveProject, setActiveBoard],
  );

  return (
    <ActiveProjectContext.Provider value={value}>{children}</ActiveProjectContext.Provider>
  );
}

export function useActiveProject(): ActiveProjectContextValue {
  const ctx = useContext(ActiveProjectContext);
  if (!ctx) {
    throw new Error('useActiveProject must be used inside <ActiveProjectProvider>');
  }
  return ctx;
}
